import * as React from 'react';
import { st, classes } from './RatingsInput.st.css';
import { RatingsInputProps, RatingsInputHandle } from './types';
import { Favorite, FavoriteFill } from '@wix/wix-ui-icons-common/on-stage';
import {
  FavoriteFillLarge,
  FavoriteLarge,
} from '@wix/wix-ui-icons-common/on-stage/system';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { RatingsInputDataHooks, RatingsInputDataKeys } from './dataHooks';
import uniqueId from '../../common/uniqueId';

/** Rating input is used to collect measurable feedback from users. */
export const RatingsInput = React.forwardRef<
  RatingsInputHandle,
  RatingsInputProps
>(
  (
    {
      className,
      value,
      disabled,
      onSelect = () => {},
      label,
      size = 'small',
      labelLayout = 'labelTop',
      errorMessage,
      errorAppearance,
      getReactionLabel,
      showReactionLabel = true,
      ...props
    },
    ref,
  ) => {
    const [hoveredRating, setHoveredRating] = React.useState<
      number | undefined
    >();
    const errorMessageId = React.useMemo(
      () => uniqueId('error-message-id'),
      [],
    );
    const ratingName = React.useMemo(() => uniqueId('rating-name'), []);
    const ratingItems = React.useRef(new Map());

    React.useImperativeHandle(
      ref,
      (): RatingsInputHandle => ({
        focus: (options?: FocusOptions) => {
          ratingItems.current.get(1).focus(options);
        },
        blur: () => {
          currentRating && ratingItems.current.get(currentRating).blur();
        },
      }),
    );

    React.useEffect(() => {
      if (
        typeof value === 'undefined' &&
        typeof hoveredRating !== 'undefined'
      ) {
        setHoveredRating(undefined);
      }
    }, [value]);

    const FilledStar = size === 'small' ? FavoriteFill : FavoriteFillLarge;
    const EmptyStar = size === 'small' ? Favorite : FavoriteLarge;

    const currentRating = hoveredRating ? hoveredRating : value;

    const setRatingRef = (ratingRef, key) => {
      if (!ratingItems.current.has(key)) {
        ratingItems.current.set(key, ratingRef);
      }
    };

    return (
      <ErrorMessageWrapper
        {...{
          'data-hook': props['data-hook'],
          visible: Boolean(errorMessage),
          message: errorMessage,
          errorAppearance,
          messageId: errorMessageId,
          className: st(
            classes.root,
            {
              layout: labelLayout,
              size,
              disabled,
              withError: Boolean(errorMessage),
            },
            className,
          ),
        }}
      >
        <fieldset
          className={classes.container}
          data-hook={RatingsInputDataHooks.Container}
          {...{
            [RatingsInputDataKeys.Size]: size,
            [RatingsInputDataKeys.Disabled]: Boolean(disabled),
            [RatingsInputDataKeys.Layout]: labelLayout,
          }}
        >
          {label ? (
            <legend
              className={classes.label}
              data-hook={RatingsInputDataHooks.Label}
            >
              {label}
            </legend>
          ) : null}
          <div className={classes.ratingsWrapper}>
            {[1, 2, 3, 4, 5].map((rating) => {
              const ratingId = `${ratingName}-rating-${rating}`;
              const isActive = rating <= currentRating;
              return (
                <div
                  key={rating}
                  data-hook={RatingsInputDataHooks.RatingStarWrapper}
                  className={classes.ratingItem}
                  onMouseEnter={() => !disabled && setHoveredRating(rating)}
                  onMouseLeave={() => !disabled && setHoveredRating(undefined)}
                  onMouseDown={() => !disabled && onSelect(rating)}
                >
                  <input
                    data-hook={RatingsInputDataHooks.RatingStar}
                    {...{
                      [RatingsInputDataKeys.Active]: isActive,
                    }}
                    id={ratingId}
                    ref={(ratingRef) => setRatingRef(ratingRef, rating)}
                    className={classes.visuallyHidden}
                    value={rating}
                    type="radio"
                    checked={rating === value}
                    name={ratingName}
                    disabled={disabled}
                    onChange={() => !disabled && onSelect(rating)}
                    aria-describedby={errorMessage && errorMessageId}
                    aria-label={getReactionLabel?.(rating)}
                  />
                  <span className={classes.icon} aria-hidden>
                    {isActive ? <FilledStar /> : <EmptyStar />}
                  </span>
                </div>
              );
            })}
          </div>
          {showReactionLabel && getReactionLabel && currentRating ? (
            <span
              className={st(classes.ratingLabel, {
                size,
              })}
              aria-hidden
              data-hook={RatingsInputDataHooks.ReactionLabel}
            >
              {getReactionLabel(currentRating)}
            </span>
          ) : null}
        </fieldset>
      </ErrorMessageWrapper>
    );
  },
);

RatingsInput.displayName = 'RatingsInput';
